(() => {
  'use strict';

  const MAX_RESULTS = 6;
  const MAX_CANDIDATES = 28;
  const BATCH_SIZE = 4;
  const WIDTHS = Array.from({ length: 59 }, (_, index) => 155 + (index * 5));
  const ASPECTS = Array.from({ length: 13 }, (_, index) => 20 + (index * 5));

  const number = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const rimLabel = (value) => Number.isInteger(number(value)) ? String(number(value)) : String(number(value));
  const sizeLabel = (size) => `${Math.round(size.width)}/${Math.round(size.aspect)}R${rimLabel(size.rim)}`;
  const signed = (value, places = 2, suffix = '') => `${value > 0 ? '+' : ''}${value.toFixed(places)}${suffix}`;

  function calculateMetric(size) {
    const widthInches = number(size.width) / 25.4;
    const sidewall = widthInches * (number(size.aspect) / 100);
    const diameter = number(size.rim) + (sidewall * 2);
    if (![widthInches, sidewall, diameter].every(Number.isFinite) || diameter <= 0) return null;
    return { diameter, metricWidth: number(size.width), metricAspect: number(size.aspect) };
  }

  function normalizeText(value) {
    return String(value || '')
      .toUpperCase()
      .replace(/ZR/g, 'R')
      .replace(/\s+/g, '')
      .replace(/R-/g, 'R');
  }

  function productText(product) {
    const variants = Array.isArray(product && product.variants)
      ? product.variants.map((variant) => typeof variant === 'string' ? variant : variant && variant.title)
      : [];
    return [
      product && product.title,
      product && product.type,
      product && product.product_type,
      ...variants
    ].filter(Boolean).join(' ');
  }

  function isTireProduct(product) {
    const type = `${product && product.type || ''} ${product && product.product_type || ''}`.toLowerCase();
    const title = String(product && product.title || '').toLowerCase();
    return type.includes('tire') || /\b(tire|radial|slick|front runner)\b/.test(title);
  }

  function productContainsExactSize(product, size) {
    if (!isTireProduct(product)) return false;
    return normalizeText(productText(product)).includes(normalizeText(sizeLabel(size)));
  }

  async function catalogHasSize(instance, size) {
    const key = sizeLabel(size);
    instance.__smgCatalogCache ||= new Map();
    if (instance.__smgCatalogCache.has(key)) return instance.__smgCatalogCache.get(key);

    const lookup = (async () => {
      if (typeof fetch !== 'function') throw new Error('Catalog lookup unavailable');
      const config = document.querySelector('[data-smg-tire-catalog-guard]');
      const configuredPath = config && config.dataset.predictiveSearchUrl;
      const basePath = String(configuredPath || '/search/suggest')
        .replace(/\.json(?:\?.*)?$/i, '')
        .replace(/\/$/, '');
      const queries = [key, key.replace(/[\/R]/g, ' ')];

      for (const query of queries) {
        const url = new URL(`${basePath}.json`, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[options][unavailable_products]', 'show');
        url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title');

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Catalog lookup failed: ${response.status}`);

        const payload = await response.json();
        const products = payload?.resources?.results?.products;
        if (Array.isArray(products) && products.some((product) => productContainsExactSize(product, size))) {
          return true;
        }
      }
      return false;
    })();

    instance.__smgCatalogCache.set(key, lookup);
    try {
      return await lookup;
    } catch (error) {
      instance.__smgCatalogCache.delete(key);
      throw error;
    }
  }

  function buildCandidates(instance, rim, maximumDifference) {
    const target = number(instance.comparison?.one?.diameter);
    const currentWidth = number(instance.comparison?.one?.metricWidth);
    const currentAspect = number(instance.comparison?.one?.metricAspect);
    const widthWindow = Math.min(60, Math.max(30, Math.round((currentWidth * .18) / 5) * 5));
    const candidates = [];

    for (const width of WIDTHS) {
      const widthDistance = Math.abs(width - currentWidth);
      if (widthDistance > widthWindow) continue;

      for (const aspect of ASPECTS) {
        const size = { format: 'metric', width, aspect, rim };
        const calculated = calculateMetric(size);
        if (!calculated) continue;
        const difference = ((calculated.diameter - target) / target) * 100;
        if (Math.abs(difference) > maximumDifference) continue;

        const widthTier = widthDistance <= 20 ? 0 : widthDistance <= 40 ? 1 : 2;
        const widthPercent = currentWidth ? (widthDistance / currentWidth) * 100 : 0;
        const aspectDistance = Math.abs(aspect - currentAspect);
        const score = (widthTier * 2.25)
          + (Math.abs(difference) * 1.75)
          + (widthPercent * .12)
          + (aspectDistance * .015);

        candidates.push({ size, calculated, difference, widthDistance, widthTier, score });
      }
    }

    candidates.sort((a, b) => {
      if (a.widthTier !== b.widthTier) return a.widthTier - b.widthTier;
      if (Math.abs(a.score - b.score) > .001) return a.score - b.score;
      if (Math.abs(Math.abs(a.difference) - Math.abs(b.difference)) > .001) {
        return Math.abs(a.difference) - Math.abs(b.difference);
      }
      return a.widthDistance - b.widthDistance;
    });

    const unique = [];
    const seen = new Set();
    for (const candidate of candidates) {
      const label = sizeLabel(candidate.size);
      if (seen.has(label)) continue;
      seen.add(label);
      unique.push(candidate);
      if (unique.length >= MAX_CANDIDATES) break;
    }
    return unique;
  }

  async function renderCatalogAlternates() {
    const container = this.querySelector('[data-alternate-list]');
    const select = this.querySelector('[data-alternate-wheel]');
    if (!container || !select || !this.comparison) return;

    const requestId = (this.__smgAlternateRequestId || 0) + 1;
    this.__smgAlternateRequestId = requestId;
    const rim = number(select.value, this.comparison.two.rim);
    let candidates = buildCandidates(this, rim, 2.01);
    if (candidates.length < 12) candidates = buildCandidates(this, rim, 3.01);

    if (!candidates.length) {
      container.innerHTML = '<div class="smg-tire-lab__alternate-empty">No close mathematical matches were found for this wheel diameter. Try another wheel size or ask SMG for a fitment check.</div>';
      return;
    }

    container.setAttribute('aria-busy', 'true');
    container.innerHTML = '<div class="smg-tire-lab__alternate-loading"><span class="smg-tire-lab__alternate-spinner" aria-hidden="true"></span><strong>Checking real tire sizes in the SMG catalog…</strong><small>Only exact sizes represented by storefront tire products will be shown.</small></div>';

    const validated = [];
    let failedLookups = 0;
    for (let index = 0; index < candidates.length && validated.length < MAX_RESULTS; index += BATCH_SIZE) {
      const batch = candidates.slice(index, index + BATCH_SIZE);
      const results = await Promise.allSettled(batch.map((candidate) => catalogHasSize(this, candidate.size)));
      if (requestId !== this.__smgAlternateRequestId) return;
      results.forEach((result, resultIndex) => {
        if (result.status === 'fulfilled' && result.value === true) validated.push(batch[resultIndex]);
        if (result.status === 'rejected') failedLookups += 1;
      });
    }

    if (requestId !== this.__smgAlternateRequestId) return;
    container.removeAttribute('aria-busy');

    if (!validated.length) {
      container.innerHTML = failedLookups >= candidates.length
        ? '<div class="smg-tire-lab__alternate-empty"><strong>Catalog lookup is temporarily unavailable.</strong><br>No unverified sizes are being displayed. Use tire search or ask SMG to confirm a size.</div>'
        : '<div class="smg-tire-lab__alternate-empty"><strong>No close alternate sizes were found in the current SMG tire catalog.</strong><br>Try another wheel diameter or ask SMG for a vehicle-specific recommendation.</div>';
      return;
    }

    const currentWidth = number(this.comparison.one.metricWidth);
    container.innerHTML = validated.slice(0, MAX_RESULTS).map(({ size, calculated, difference }, index) => {
      const widthChange = size.width - currentWidth;
      const widthCopy = Math.abs(widthChange) < 1 ? 'same nominal width' : `${widthChange > 0 ? '+' : ''}${Math.round(widthChange)} mm width`;
      return `<button type="button" class="smg-tire-lab__alternate-item" data-alternate-size="${sizeLabel(size)}"><strong>${sizeLabel(size)}${index === 0 ? '<em>Best catalog match</em>' : ''}</strong><span>${calculated.diameter.toFixed(2)} in diameter • ${signed(difference)} • ${widthCopy}</span><small>Exact size found in SMG tire catalog</small></button>`;
    }).join('');
  }

  function installPatch() {
    const Constructor = customElements.get('smg-tire-size-lab');
    if (!Constructor || Constructor.prototype.__smgCatalogGuardInstalled) return;
    Constructor.prototype.__smgCatalogGuardInstalled = true;
    Constructor.prototype.renderAlternates = renderCatalogAlternates;
    document.querySelectorAll('smg-tire-size-lab').forEach((instance) => instance.renderAlternates());
  }

  if (window.customElements) {
    customElements.whenDefined('smg-tire-size-lab').then(installPatch);
  }
})();
